import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Application {
  appId: string;
  name: string;
  description: string | null;
  orgId: string;
  status: 'active' | 'suspended' | 'retired';
  allowedModels?: string[] | null;
  budgetConfig?: Record<string, unknown> | null;
  agentIds?: string[];
  agentCount?: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateApplicationRequest {
  name: string;
  description?: string;
  allowed_models?: string[];
  budget_config?: Record<string, unknown>;
}

export const applicationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getApplications: builder.query<Application[], void>({
      query: () => '/portal/applications',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ applications: Application[] }>(response);
        return normalized.applications ?? [];
      },
      providesTags: ['Application'],
    }),

    getApplication: builder.query<Application, string>({
      query: (appId) => `/portal/applications/${appId}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<Application>(response),
      providesTags: ['Application'],
    }),

    createApplication: builder.mutation<Application, CreateApplicationRequest>({
      query: (body) => ({
        url: '/portal/applications',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => normalizeKeys<Application>(response),
      invalidatesTags: ['Application'],
    }),

    updateApplication: builder.mutation<
      Application,
      { appId: string; body: Partial<CreateApplicationRequest> & { status?: string } }
    >({
      query: ({ appId, body }) => ({
        url: `/portal/applications/${appId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => normalizeKeys<Application>(response),
      invalidatesTags: ['Application'],
    }),

    deleteApplication: builder.mutation<{ deleted: boolean }, string>({
      query: (appId) => ({
        url: `/portal/applications/${appId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Application'],
    }),

    updateApplicationAgents: builder.mutation<
      void,
      { appId: string; agentIds: string[] }
    >({
      query: ({ appId, agentIds }) => ({
        url: `/portal/applications/${appId}/agents`,
        method: 'PUT',
        body: { agent_ids: agentIds },
      }),
      invalidatesTags: ['Application'],
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useGetApplicationQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
  useUpdateApplicationAgentsMutation,
} = applicationsApi;
